import { useCallback } from "react";
import { trackEvent } from "common/analytics";
import { applyUserIdentity, type AuthUserLike } from "@/analytics/identity";
import { useMarkNewUserForOnboarding } from "@/features/onboarding";
import { clearPendingVerification } from "@/features/auth/pendingVerification";

/** The `data` of a sign-in reply: identity fields plus the first-login flag. */
export type AfterLoginUserData = AuthUserLike & { firstLogin?: number };

/**
 * What every email-based sign-in does once the session is committed: identify,
 * count the login, and flag a brand-new account for the onboarding flow. Shared
 * by the password screen and the two verify-and-sign-in paths.
 */
export function useAfterLogin() {
  /*
   * A first login means a brand-new account, which is owed the new onboarding
   * flow (`@/features/onboarding`) - not the legacy "Before you use
   * Sociability" carousel this used to open. That carousel is still reachable
   * from the profile menu's "How the app works"; it is a help slider, not the
   * questions the app needs answering.
   *
   * Marking rather than opening: the flow must not appear over the auth modal, so
   * `onboardingV2PresentationAtom` holds it until the modal closes.
   */
  const markNewUserForOnboarding = useMarkNewUserForOnboarding();

  return useCallback(
    (userData: AfterLoginUserData) => {
      // Identify BEFORE the event so account_logged_in carries the real user id rather than the
      // anonymous $device id (or a stale numeric id left over from a prior unverified signup).
      // identify() updates the distinct id synchronously, and applyUserIdentity resets first when
      // switching accounts so the two identities never merge.
      // email_verified is asserted here because every path that reaches this has just proven it:
      // the backend rejects /user/login while email_verification is empty, and the verify paths
      // only hand out a session after stamping it.
      applyUserIdentity(userData, { email_verified: true });

      // Email/password login and email verification both count as the "email" provider.
      trackEvent("account_logged_in", { auth_provider: "email" });

      if (userData.firstLogin == 1) {
        markNewUserForOnboarding(true);
      }

      clearPendingVerification();
    },
    [markNewUserForOnboarding],
  );
}
